// TRUE OR FALSE (მართალია თუ ტყუილი) — fast quiz, everyone taps TRUE / FALSE.

import { ADULT_CATS, TRUF_CATS, type TrufItem } from "../content";
import {
  activePlayers, bool, clampInt, oneOf, stringList, type BotPlan, type Engine, type EngineCtx, type TimedState,
} from "../engine";
import type { ErrorCode, GameResultSummary, Lang, RoomCore } from "../types";

export interface TrufSettings {
  count: number; // 10 | 15 | 20 | 30
  time: number; // seconds per statement: 7 | 10 | 15
  cats: string[];
  adult: boolean;
}

type Phase = "intro" | "question" | "reveal" | "over";

interface Answer { v: boolean; at: number; }

export interface TrufState extends TimedState {
  phase: Phase;
  items: TrufItem[];
  i: number;
  answers: Record<string, Answer>;
  scores: Record<string, number>;
  streak: Record<string, number>;
  best: Record<string, number>; // best streak
  correct: Record<string, number>;
  gained: Record<string, number>; // points from the last statement
  qStart: number;
}

const INTRO_MS = 2_400;
const REVEAL_MS = 5_500;
export const TRUF_POINTS = { base: 100, speed: 100, streakStep: 25, streakMax: 100 } as const;

function setPhase(s: TrufState, phase: Phase, now: number, ms: number | null) {
  s.phase = phase;
  s.deadline = ms === null ? null : now + ms;
  s.phaseMs = ms ?? 0;
}

function pick(room: RoomCore<TrufSettings>, ctx: EngineCtx): TrufItem[] | null {
  const all = ctx.content.truf(room.lang);
  const cats = room.settings.cats.length ? new Set(room.settings.cats) : null;
  let pool = all.filter((it) => (room.settings.adult || !it.adult) && (!cats || cats.has(it.cat)));
  if (!room.settings.adult) pool = pool.filter((it) => !ADULT_CATS.has(it.cat));
  if (pool.length < room.settings.count) pool = all.filter((it) => room.settings.adult || !it.adult);
  if (pool.length < 3) return null;
  const used = new Set(room.usedContent);
  const fresh = pool.filter((it) => !used.has(it.id));
  const src = fresh.length >= room.settings.count ? fresh : pool;
  // mix true/false so streaks of the same answer don't give the game away
  const trues = ctx.rng.shuffle(src.filter((x) => x.t));
  const falses = ctx.rng.shuffle(src.filter((x) => !x.t));
  const out: TrufItem[] = [];
  while (out.length < room.settings.count && (trues.length || falses.length)) {
    const takeTrue = trues.length && (!falses.length || ctx.rng.next() < 0.5);
    out.push((takeTrue ? trues : falses).shift()!);
  }
  return out;
}

function score(room: RoomCore<TrufSettings>, s: TrufState) {
  const item = s.items[s.i];
  const total = room.settings.time * 1000;
  s.gained = {};
  for (const p of activePlayers(room)) {
    const ans = s.answers[p.id];
    if (ans && ans.v === item.t) {
      const left = Math.max(0, total - (ans.at - s.qStart));
      const speed = Math.round((TRUF_POINTS.speed * left) / total);
      const st = (s.streak[p.id] ?? 0) + 1;
      s.streak[p.id] = st;
      s.best[p.id] = Math.max(s.best[p.id] ?? 0, st);
      const bonus = Math.min(TRUF_POINTS.streakMax, Math.max(0, st - 2) * TRUF_POINTS.streakStep);
      const pts = TRUF_POINTS.base + speed + bonus;
      s.scores[p.id] = (s.scores[p.id] ?? 0) + pts;
      s.correct[p.id] = (s.correct[p.id] ?? 0) + 1;
      s.gained[p.id] = pts;
    } else {
      s.streak[p.id] = 0;
      s.gained[p.id] = 0;
    }
  }
}

function goReveal(room: RoomCore<TrufSettings>, s: TrufState, ctx: EngineCtx) {
  score(room, s);
  setPhase(s, "reveal", ctx.now, REVEAL_MS);
}

export const trufEngine: Engine<TrufState, TrufSettings> = {
  id: "truf",
  minPlayers: 1,
  maxPlayers: 30,
  supportsBots: true,

  defaults(_lang: Lang): TrufSettings {
    return { count: 15, time: 10, cats: [], adult: false };
  },

  sanitize(input: any, prev: TrufSettings): TrufSettings {
    const i = input && typeof input === "object" ? input : {};
    return {
      count: oneOf(clampInt(i.count, 5, 30, prev.count), [10, 15, 20, 30] as const, prev.count as 15),
      time: oneOf(clampInt(i.time, 5, 20, prev.time), [7, 10, 15] as const, prev.time as 10),
      cats: stringList(i.cats, TRUF_CATS as unknown as string[], prev.cats),
      adult: bool(i.adult, prev.adult),
    };
  },

  start(room, ctx) {
    const items = pick(room, ctx);
    if (!items) return "no_content" as ErrorCode;
    room.usedContent.push(...items.map((x) => x.id));
    if (room.usedContent.length > 600) room.usedContent.splice(0, room.usedContent.length - 600);
    const s: TrufState = {
      phase: "intro", deadline: null, phaseMs: 0, items, i: 0, answers: {},
      scores: {}, streak: {}, best: {}, correct: {}, gained: {}, qStart: ctx.now,
    };
    for (const p of activePlayers(room)) {
      s.scores[p.id] = 0;
      s.streak[p.id] = 0;
    }
    setPhase(s, "intro", ctx.now, INTRO_MS);
    return s;
  },

  act(room, s, actorId, a, ctx) {
    if (!a || typeof a !== "object") return "bad_input";
    const player = room.players.find((p) => p.id === actorId);
    switch (a.t) {
      case "answer": {
        if (s.phase !== "question") return "bad_phase";
        if (!player || player.role !== "player") return "not_player";
        if (s.answers[actorId]) return "already";
        if (typeof a.v !== "boolean") return "bad_input";
        s.answers[actorId] = { v: a.v, at: ctx.now };
        const everyone = activePlayers(room).every((p) => s.answers[p.id]);
        if (everyone) goReveal(room, s, ctx);
        return s;
      }
      case "next": {
        if (room.hostId !== actorId && !room.local) return "not_host";
        if (s.phase === "reveal" || s.phase === "intro") return trufEngine.tick(room, s, ctx);
        if (s.phase === "question") { goReveal(room, s, ctx); return s; }
        return "bad_phase";
      }
      default:
        return "bad_input";
    }
  },

  tick(room, s, ctx) {
    if (s.phase === "intro") {
      s.answers = {};
      s.qStart = ctx.now;
      setPhase(s, "question", ctx.now, room.settings.time * 1000);
    } else if (s.phase === "question") {
      goReveal(room, s, ctx);
    } else if (s.phase === "reveal") {
      if (s.i + 1 >= s.items.length) setPhase(s, "over", ctx.now, null);
      else {
        s.i += 1;
        s.answers = {};
        s.qStart = ctx.now;
        setPhase(s, "question", ctx.now, room.settings.time * 1000);
      }
    } else {
      s.deadline = null;
    }
    return s;
  },

  onLeave(room, s, _pid, ctx) {
    if (s.phase === "question" && activePlayers(room).every((p) => s.answers[p.id])) goReveal(room, s, ctx);
    return s;
  },

  onJoin(_room, s, pid) {
    s.scores[pid] ??= 0;
    s.streak[pid] ??= 0;
    return s;
  },

  view(room, s, viewerId) {
    const item = s.items[s.i];
    const shown = s.phase === "reveal" || s.phase === "over";
    const answered = Object.keys(s.answers);
    return {
      phase: s.phase,
      deadline: s.deadline,
      phaseMs: s.phaseMs,
      i: s.i,
      total: s.items.length,
      statement: s.phase === "intro" ? null : item.s,
      cat: item.cat,
      truth: shown ? item.t : null,
      explanation: shown ? item.x : null,
      myAnswer: viewerId && s.answers[viewerId] ? s.answers[viewerId].v : null,
      answered,
      answers: shown ? Object.fromEntries(Object.entries(s.answers).map(([k, v]) => [k, v.v])) : null,
      scores: s.scores,
      gained: shown ? s.gained : {},
      streak: s.streak,
      best: s.best,
      correct: s.correct,
    };
  },

  bot(room, s, botId, ctx): BotPlan | null {
    if (s.phase !== "question" || s.answers[botId]) return null;
    const item = s.items[s.i];
    const skill = item.diff === 1 ? 0.78 : item.diff === 2 ? 0.66 : 0.55;
    const v = ctx.rng.next() < skill ? item.t : !item.t;
    const maxDelay = Math.max(1500, room.settings.time * 1000 - 1200);
    return { key: `ans:${s.i}`, delay: 900 + ctx.rng.int(maxDelay - 900), action: { t: "answer", v } };
  },

  isOver(s) {
    return s.phase === "over";
  },

  summary(room, s): GameResultSummary {
    const players = room.players
      .filter((p) => s.scores[p.id] !== undefined)
      .map((p) => ({ id: p.id, name: p.name, score: s.scores[p.id] ?? 0, userId: p.userId ?? null, isBot: p.isBot }));
    const max = Math.max(0, ...players.map((p) => p.score));
    return { game: "truf", lang: room.lang, players, winnerIds: players.filter((p) => p.score === max && max > 0).map((p) => p.id) };
  },
};
